import React, { useState, useMemo, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Badge } from "primereact/badge";
import { ProgressSpinner } from "primereact/progressspinner";
import toast from "react-hot-toast";

import { useGetTenantsQuery } from "../../store/services/tenants";
import { useGetEventsQuery, useUpdateFeaturedEventsOrderMutation } from "../../store/services/events";
import { Event } from "../../models/events";
import { getEventImageSrc } from "../../utils/functions/getEventImageSrc";

export const FeaturedEventsOrder = () => {
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  
  const { data: tenants, isLoading: isLoadingTenants } = useGetTenantsQuery({});
  const tenantOptions = useMemo(() => (
    Array.isArray(tenants) ? tenants.map(t => ({
      ...t,
      displayName: `${t.provider || t.id} ${t.emailDomain ? `(${t.emailDomain})` : ''}`.trim()
    })) : []
  ), [tenants]);

  const { data: eventsPage, isLoading: isLoadingEvents, isFetching: isFetchingEvents, refetch } = useGetEventsQuery(
    { limit: 1000, show: "next", featured: true },
    { refetchOnMountOrArgChange: true }
  );
  const allEvents = eventsPage?.events;
  const [updateOrder, { isLoading: isUpdating }] = useUpdateFeaturedEventsOrderMutation();

  const [orderedEvents, setOrderedEvents] = useState<Event[]>([]);

  useMemo(() => {
    if (!allEvents || !selectedTenantId) {
      setOrderedEvents([]);
      return;
    }

    const filtered = allEvents.filter((event) => {
      if (!event.featured || event.featuredOrder == null) return false;

      const eventTenantId = String(event.tenantId || '');
      const selectedTenantIdStr = String(selectedTenantId || '');

      return eventTenantId === selectedTenantIdStr;
    });

    filtered.sort((a, b) => (a.featuredOrder ?? 99) - (b.featuredOrder ?? 99));
    setOrderedEvents(filtered);
  }, [allEvents, selectedTenantId]);

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    dragItem.current = position;
    if (e.target instanceof HTMLElement) {
      setTimeout(() => {
        if (e.target instanceof HTMLElement) e.target.style.opacity = '0.5';
      }, 0);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    if (dragItem.current === null) return;
    if (dragItem.current !== position) {
      const copyListItems = [...orderedEvents];
      const dragItemContent = copyListItems[dragItem.current];
      copyListItems.splice(dragItem.current, 1);
      copyListItems.splice(position, 0, dragItemContent);
      dragItem.current = position;
      setOrderedEvents(copyListItems);
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement) {
      e.target.style.opacity = '1';
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleUpdate = async () => {
    if (!selectedTenantId) return;

    try {
      const payload = orderedEvents.map((evt, index) => ({
        id: evt.id,
        featuredOrder: index + 1
      }));

      await updateOrder({
        tenantId: selectedTenantId,
        events: payload
      }).unwrap();
      toast.success("Orden de eventos destacados actualizado exitosamente");
      refetch();
    } catch (error) {
      toast.error("Ocurrió un error al actualizar el orden");
    }
  };

  return (
    <div className="p-card gap-4 p-3">
      <div className="flex w-full justify-content-center align-items-center gap-3 mt-4 mb-6">
        <h1 className="text-center m-0">Editar orden de eventos destacados</h1>
      </div>

      <div className="grid">
        <div className="col-12 md:col-6 mb-4">
          <span className="p-float-label">
            <Dropdown
              className="w-full"
              id="tenantId"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.value)}
              options={tenantOptions}
              optionLabel="displayName"
              optionValue="id"
              loading={isLoadingTenants}
              placeholder="Seleccione un tenant"
            />
            <label htmlFor="tenantId">Tenant</label>
          </span>
          <small className="text-color-secondary block mt-2">
            Seleccione el Tenant para ordenar sus eventos destacados activos. Los eventos con la inscripción cerrada ya no retienen el orden.
          </small>
        </div>

        <div className="col-12">
          {selectedTenantId ? (
            <>
              {isFetchingEvents || isLoadingEvents ? (
                <div className="flex flex-column justify-content-center align-items-center p-5">
                  <ProgressSpinner
                    style={{ width: '50px', height: '50px' }}
                    strokeWidth="8"
                  />
                  <style>{`
                    .p-progress-spinner-circle {
                      stroke: #10B981 !important;
                      animation: p-progress-spinner-dash 1.5s ease-in-out infinite !important;
                    }
                  `}</style>
                  <p className="text-600 mt-3 m-0">Cargando eventos destacados...</p>
                </div>
              ) : orderedEvents.length > 0 ? (
                <>
                  <div className="flex justify-content-between mb-2 px-3 text-600 font-semibold w-full">
                    <div style={{ width: '4rem' }} className="text-center">Orden</div>
                    <div className="flex-grow-1 pl-4">Nombre del Evento</div>
                    <div style={{ width: '10rem' }} className="text-center">Orden Anterior</div>
                  </div>

                  <div className="flex flex-column gap-2 mb-4 w-full">
                    {orderedEvents.map((event, index) => (
                      <div
                        key={event.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        className="flex align-items-center p-3 border-1 border-300 border-round shadow-1 surface-card cursor-move transition-colors hover:surface-hover"
                      >
                        <div className="flex align-items-center justify-content-center bg-primary text-white font-bold border-circle min-w-min w-3rem h-3rem mr-4 shadow-2 select-none flex-shrink-0">
                          {index + 1}
                        </div>

                        <div
                          className="mr-4 border-round overflow-hidden shadow-1 flex-shrink-0 bg-100"
                          style={{ width: '80px', height: '80px' }}
                        >
                          <img
                            src={event.imageUrls && event.imageUrls.length > 0 ? event.imageUrls[0] : getEventImageSrc(event.imageFormat, event.imageBase64)}
                            alt={event.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => (e.currentTarget.src = "/assets/images/Image-not-found.png")}
                          />
                        </div>

                        <div className="flex-grow-1 flex flex-column gap-2 select-none overflow-hidden pr-3">
                          <span className="font-bold text-xl text-800 line-height-2 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                            {event.name} <span className="text-sm font-normal text-500">#{event.id}</span>
                          </span>

                          <div className="flex align-items-center flex-wrap gap-4 text-sm text-600 font-medium">
                            {event.startDate && (
                              <span className="flex align-items-center gap-2">
                                <i className="pi pi-calendar text-primary"></i>
                                {new Date(event.startDate).toLocaleString('es-AR', {
                                  weekday: 'short',
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            )}
                            {event.space && (
                              <span className="flex align-items-center gap-2">
                                <i className="pi pi-map-marker text-primary"></i>
                                <span className="white-space-nowrap overflow-hidden text-overflow-ellipsis" style={{ maxWidth: '200px' }} title={event.space}>
                                  {event.space}
                                </span>
                              </span>
                            )}
                            <span className="flex align-items-center gap-2">
                              <i className="pi pi-users text-primary"></i>
                              {event.available ?? '-'} / {event.amount ?? '-'} cupos
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-2 mt-1">
                            {event.eventTypeName && <Badge value={event.eventTypeName} severity="info" />}
                            {event.active ? <Badge value="Activo" severity="success" /> : <Badge value="Inactivo" severity="danger" />}
                            {event.price ? <Badge value={`$${event.price}`} /> : <Badge value="Gratis" severity="success" />}
                          </div>
                        </div>

                        <div className="flex justify-content-center align-items-center flex-shrink-0" style={{ width: '10rem' }}>
                          <div className="text-500 font-semibold w-4rem text-center select-none text-xl">
                            {event.featuredOrder ?? '-'}
                          </div>
                          <div className="text-400 ml-4">
                            <i className="pi pi-bars text-2xl"></i>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-content-end w-full">
                    <Button
                      label="Actualizar Orden"
                      icon="pi pi-save"
                      onClick={handleUpdate}
                      loading={isUpdating}
                    />
                  </div>
                </>
              ) : (
                <div className="text-center p-5 border-1 surface-border border-round">
                  <i className="pi pi-inbox text-5xl text-400 mb-3"></i>
                  <p className="text-600 m-0">No hay eventos destacados vigentes para esta selección.</p>
                </div>
              )}
            </>
          ) : (
            <div className="p-message p-component p-message-info w-full" role="alert">
              <div className="p-message-wrapper">
                <span className="p-message-icon pi pi-info-circle"></span>
                <div className="p-message-text">Seleccione un tenant para visualizar y ordenar sus eventos destacados.</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
